/**
 * Eigene Mahlzeiten (16.10.2026): Reihenfolge, Uhrzeit und Erinnerung je
 * Mahlzeit, bis zu drei weitere Snacks, und wo ein Training im Tag steht.
 *
 * Gespeichert am Profil (`profiles.meals`, `null` = die sechs festen in der
 * gewohnten Reihenfolge, ohne Uhrzeit). Rezepte behalten ihre Kategorie aus
 * `MEAL_SLOTS` — „Snack 2“ ist ein Platz im Tag, keine Art von Gericht.
 */

import { stableId } from './ids.js';
import { DAY_SLOTS, EXTRA_SNACKS, MEAL_SLOTS } from './nutrition.js';
import type { DaySlot, MealSlot } from './nutrition.js';
import { isWallTime } from './time.js';

export function isDaySlot(value: unknown): value is DaySlot {
  return typeof value === 'string' && (DAY_SLOTS as readonly string[]).includes(value);
}

export function isExtraSnack(slot: DaySlot): boolean {
  return (EXTRA_SNACKS as readonly string[]).includes(slot);
}

/**
 * Pre- und Post-Workout (18.10.2026): liegen immer um das Training, haben
 * keine eigene Uhrzeit und keine eigene Stelle — und fehlen an Tagen ohne
 * Training. Wer zu einer festen Zeit etwas essen will, nimmt einen Snack.
 */
export function isWorkoutMeal(slot: DaySlot): boolean {
  return slot === 'pre' || slot === 'post';
}

export interface MealSetting {
  slot: DaySlot;
  /** `HH:MM` in Ortszeit — oder keine Uhrzeit. */
  time: string | null;
  /** Erinnerung zur Uhrzeit; ohne Uhrzeit immer aus. */
  reminder: boolean;
}

/** Die sechs festen Mahlzeiten, wie vor dem 16.10.2026. */
export const DEFAULT_MEALS: readonly MealSetting[] = MEAL_SLOTS.map((slot) => ({
  slot,
  time: null,
  reminder: false,
}));

/**
 * Gespeichertes lesen — tolerant: Unbekanntes und Doppeltes fällt weg, eine
 * fehlende feste Mahlzeit kommt an ihre gewohnte Stelle zurück (sonst
 * verschwände, was dort protokolliert ist), eine Erinnerung ohne Uhrzeit ist aus.
 */
export function mealSettings(stored: unknown): MealSetting[] {
  if (!Array.isArray(stored)) return DEFAULT_MEALS.map((meal) => ({ ...meal }));
  const seen = new Set<DaySlot>();
  const list: MealSetting[] = [];
  for (const raw of stored as unknown[]) {
    if (!raw || typeof raw !== 'object') continue;
    const entry = raw as Record<string, unknown>;
    if (!isDaySlot(entry.slot) || seen.has(entry.slot)) continue;
    seen.add(entry.slot);
    // Pre/Post haben keine Uhrzeit (mehr) — auch keine gespeicherte von früher.
    const time =
      typeof entry.time === 'string' && isWallTime(entry.time) && !isWorkoutMeal(entry.slot)
        ? entry.time
        : null;
    list.push({ slot: entry.slot, time, reminder: time !== null && entry.reminder === true });
  }
  MEAL_SLOTS.forEach((slot, index) => {
    if (seen.has(slot)) return;
    list.splice(Math.min(index, list.length), 0, { slot, time: null, reminder: false });
  });
  return list;
}

/**
 * Die Reihenfolge im Tag (18.10.2026): Mahlzeiten **mit** Uhrzeit stehen nach
 * der Uhrzeit — sie nehmen der Reihe nach die Plätze ein, die Mahlzeiten mit
 * Uhrzeit in deiner Liste haben. Mahlzeiten ohne Uhrzeit bleiben, wo du sie
 * hingestellt hast. Pre/Post gehören nicht dazu (sie liegen ums Training)
 * und stehen am Ende.
 */
export function orderedMeals(list: readonly MealSetting[]): MealSetting[] {
  const own = list.filter((meal) => !isWorkoutMeal(meal.slot));
  const timed = own
    .filter((meal) => meal.time !== null)
    .sort((a, b) => a.time!.localeCompare(b.time!));
  let next = 0;
  const ordered = own.map((meal) => (meal.time !== null ? timed[next++]! : meal));
  return [...ordered, ...list.filter((meal) => isWorkoutMeal(meal.slot))].map((meal) => ({
    ...meal,
  }));
}

/** Verschieben geht nur ohne Uhrzeit — mit Uhrzeit entscheidet die Uhrzeit. */
export function canMoveMeal(meal: MealSetting): boolean {
  return meal.time === null && !isWorkoutMeal(meal.slot);
}

/**
 * Eine Mahlzeit ohne Uhrzeit eine Stelle nach oben (-1) oder unten (+1) — in
 * der Reihenfolge, wie der Tag sie zeigt. Gespeichert wird diese Reihenfolge.
 */
export function moveMeal(list: readonly MealSetting[], slot: DaySlot, step: -1 | 1): MealSetting[] {
  const next = orderedMeals(list);
  const own = next.filter((meal) => !isWorkoutMeal(meal.slot));
  const from = own.findIndex((meal) => meal.slot === slot);
  const to = from + step;
  if (from < 0 || !canMoveMeal(own[from]!) || to < 0 || to >= own.length) return next;
  [own[from], own[to]] = [own[to]!, own[from]!];
  return orderedMeals([...own, ...next.filter((meal) => isWorkoutMeal(meal.slot))]);
}

/** Den nächsten freien Snack anhängen — oder `null`, wenn schon drei da sind. */
export function addSnack(list: readonly MealSetting[]): MealSetting[] | null {
  const free = EXTRA_SNACKS.find((slot) => !list.some((meal) => meal.slot === slot));
  if (!free) return null;
  return [...list.map((meal) => ({ ...meal })), { slot: free, time: null, reminder: false }];
}

/** Nur weitere Snacks lassen sich entfernen; die sechs festen bleiben. */
export function removeSnack(list: readonly MealSetting[], slot: DaySlot): MealSetting[] {
  if (!isExtraSnack(slot)) return list.map((meal) => ({ ...meal }));
  return list.filter((meal) => meal.slot !== slot).map((meal) => ({ ...meal }));
}

export function setMealTime(
  list: readonly MealSetting[],
  slot: DaySlot,
  time: string | null,
): MealSetting[] {
  const valid = time !== null && isWallTime(time) ? time : null;
  return list.map((meal) =>
    meal.slot === slot
      ? { ...meal, time: valid, reminder: valid ? meal.reminder : false }
      : { ...meal },
  );
}

export function setMealReminder(
  list: readonly MealSetting[],
  slot: DaySlot,
  on: boolean,
): MealSetting[] {
  return list.map((meal) =>
    meal.slot === slot ? { ...meal, reminder: on && meal.time !== null } : { ...meal },
  );
}

/** Für morgen ausgelassen? Feste Kennung je Konto, Tag und Mahlzeit. */
export function mealSkipId(userId: string, date: string, slot: DaySlot): string {
  return stableId(`meal-skip|${userId}|${date}|${slot}`);
}

/**
 * Protokollzeile eines Planeintrags (18.10.2026): Geplantes zählt als
 * gegessen. Fest je Planeintrag — legen zwei Geräte sie an, ist es dieselbe
 * Zeile; Auslassen löscht weich, Zurücknehmen belebt sie wieder. Die
 * Plan-Kennung ist schon eine Zufalls-UUID des Kontos, das Konto steckt darin.
 */
export function plannedLogId(planEntryId: string): string {
  return stableId(`planned-log|${planEntryId}`);
}

/** Erinnerung an eine Mahlzeit — eine je Konto, Tag und Mahlzeit. */
export function mealReminderRef(userId: string, date: string, slot: DaySlot): string {
  return stableId(`meal-reminder|${userId}|${date}|${slot}`);
}

export type TimelineItem<T, S = never> =
  | { kind: 'meal'; meal: MealSetting }
  | { kind: 'training'; training: T }
  | { kind: 'supplements'; time: string; supplements: S[] };

/**
 * Der Tagesablauf (18.10.2026):
 *
 * - Mahlzeiten in der Reihenfolge von `orderedMeals` (mit Uhrzeit nach der
 *   Uhrzeit, ohne Uhrzeit, wo du sie hingestellt hast);
 * - Trainings und Supplements mit Uhrzeit stehen vor der ersten Mahlzeit,
 *   deren Uhrzeit später ist — gibt es keine, am Ende; Mahlzeiten ohne
 *   Uhrzeit verschieben nichts. Supplements derselben Uhrzeit stehen
 *   zusammen;
 * - Pre direkt vor, Post direkt nach dem ersten Training. An Tagen ohne
 *   Training fehlen sie — außer es steht schon etwas darin (`keep`), dann
 *   stehen sie am Ende: Protokolliertes verschwindet nie still.
 */
export function dayTimeline<T extends { time: string }, S extends { time: string } = never>(
  meals: readonly MealSetting[],
  trainings: readonly T[],
  supplements: readonly S[] = [],
  keep: ReadonlySet<DaySlot> = new Set(),
): TimelineItem<T, S>[] {
  type Timed = { kind: 'training'; training: T } | { kind: 'supplement'; supplement: S };
  const timeOf = (entry: Timed) =>
    entry.kind === 'training' ? entry.training.time : entry.supplement.time;
  const pending: Timed[] = [
    ...trainings.map((training) => ({ kind: 'training' as const, training })),
    ...supplements.map((supplement) => ({ kind: 'supplement' as const, supplement })),
  ].sort((a, b) => timeOf(a).localeCompare(timeOf(b)));

  const ordered = orderedMeals(meals);
  const own = ordered.filter((meal) => !isWorkoutMeal(meal.slot));
  const pre = ordered.find((meal) => meal.slot === 'pre');
  const post = ordered.find((meal) => meal.slot === 'post');
  const withTraining = trainings.length > 0;

  const items: TimelineItem<T, S>[] = [];
  let first = true;
  const push = (entry: Timed) => {
    if (entry.kind === 'training') {
      if (first && pre) items.push({ kind: 'meal', meal: pre });
      items.push({ kind: 'training', training: entry.training });
      if (first && post) items.push({ kind: 'meal', meal: post });
      first = false;
      return;
    }
    const last = items.at(-1);
    if (last?.kind === 'supplements' && last.time === entry.supplement.time) {
      last.supplements.push(entry.supplement);
    } else {
      items.push({
        kind: 'supplements',
        time: entry.supplement.time,
        supplements: [entry.supplement],
      });
    }
  };
  for (const meal of own) {
    while (pending.length > 0 && meal.time !== null && timeOf(pending[0]!) < meal.time) {
      push(pending.shift()!);
    }
    items.push({ kind: 'meal', meal });
  }
  for (const entry of pending.splice(0)) push(entry);
  if (!withTraining) {
    for (const meal of [pre, post]) {
      if (meal && keep.has(meal.slot)) items.push({ kind: 'meal', meal });
    }
  }
  return items;
}

/** Der Kategorie-Slot eines Platzes — für Vorschläge („Snack 3“ → Snack-Gerichte). */
export function recipeSlotOf(slot: DaySlot): MealSlot {
  return isExtraSnack(slot) ? 'snack' : (slot as MealSlot);
}
